<template>
  <div :class="$style.container">
    <section :class="$style.modalContent">
      <HeadingHuge
        :class="$style.alignSelfCenter"
        noMargin
      >
        Thank You!
      </HeadingHuge>

      <ParagraphBody
        bodySize="large"
        :class="$style.alignSelfCenter"
      >
        <span
          v-if="showUserLimitWarning"
          data-test-id="PaymentOutroModal__newCustomerSubheading"
        >
          Here’s what you can expect next:
        </span>
        <span
          v-else
          data-test-id="PaymentOutroModal__paymentUpdateSubHeaderText"
        >
          Your payment information was successfully updated.
        </span>
      </ParagraphBody>

      <div
        v-if="showUserLimitWarning"
        :class="$style.checklistContainer"
      >
        <div
          v-for="(step, index) in checkoutSteps"
          :key="index"
          :class="$style.checklistRow"
          :data-test-id="`PaymentOutroModal__step_${index}`"
        >
          <div
            :class="$style.checkoutLine"
          >
            <CustomIcon
              :class="$style.checkoutIcon"
              :scale="1.5"
            >
              <FilledIcon v-if="step.isActive" />
              <CompleteIcon v-else />
            </CustomIcon>
            <div
              v-if="showVerticalLine(step)"
              :class="$style.verticalLine"
              :data-test-id="$testId('checkoutStepVerticalLine')"
            />
          </div>
          <div>
            {{ step.text }}
          </div>
        </div>
        <div
          :class="$style.checklistRow"
          data-test-id="PaymentOutroModal__lastStep"
        >
          <CustomIcon
            v-if="!isAnnual
              || (isAnnualWithAutomaticSelfServeFlagOff)"
            :class="$style.checkoutIcon"
            :data-test-id="$testId('checkoutStepCustomIcon')"
            :scale="1.5"
          >
            <UncompleteIcon />
          </CustomIcon>
          <div v-if="isAnnualWithAutomaticSelfServeFlagOff">
            <ParagraphBody>
              <strong>Sign your agreement.</strong> We will send you a DocuSign
              agreement to <strong>{{ user.email }}</strong>. Please allow 1
              business day for the agreement to arrive.
            </ParagraphBody>
            <ParagraphBody>
              If you do not complete the agreement, we will bill you on a month
              to month basis at the monthly rate according to our
              <ExternalLink
                :class="$style.link"
                href="https://jumpcloud.com/legal"
                :showExternalLinkIcon="false"
                text="Terms and Conditions."
                @click="handlePaymentLinkClicked('/legal', modalName)"
              />
            </ParagraphBody>
          </div>
          <div v-else-if="!isAnnual">
            Your account is <strong>STILL FREE</strong> until you add your 11th user.
          </div>
        </div>
        <ParagraphBody
          v-if="$hasFeatureFlag($featureFlags.automaticAnnualSelfServe)"
          :class="$style.haveQuestionsText"
          :data-test-id="$testId('haveQuestionsText')"
        >
          Have questions? View our
          <ExternalLink
            :class="$style.link"
            href="https://jumpcloud.com/legal"
            :showExternalLinkIcon="false"
            text="Terms and Conditions."
            @click="handlePaymentLinkClicked('/legal', modalName)"
          />
        </ParagraphBody>
      </div>

      <ParagraphBody
        :class="!showUserLimitWarning ? $style.biggerGap : ''"
        weight="bold"
      >
        To change your billing or credit card information, update it in “Accounts.”
      </ParagraphBody>
      <ParagraphBody
        v-if="isAnnualWithAutomaticSelfServeFlagOn"
        :data-test-id="$testId('youWillBeBilledText')"
      >
        <span
          data-test-id="PaymentOutroModal__annualDisclaimer"
        >
          You will be billed on the 1st of the month. If you have any questions regarding billing
          please contact
        </span>
        <ExternalLink
          :class="$style.link"
          href="mailto:billing@jumpcloud.com"
          :showExternalLinkIcon="false"
          text="billing@jumpcloud.com"
          @click="handlePaymentLinkClicked('billing@jumpcloud.com', modalName)"
        />.
      </ParagraphBody>
      <ParagraphBody
        v-else
        bodySize="small"
      >
        <span
          v-if="isAnnual"
          data-test-id="PaymentOutroModal__annualDisclaimer"
        >
          Your subscription is valid for one year.
        </span>
        <span
          v-else
          data-test-id="PaymentOutroModal__monthlyDisclaimer"
        >
          <span
            v-if="showUserLimitWarning"
            data-test-id="PaymentOutroModal__purchaseSuccessfulChargeInfoForVfa"
          >
            Once you add your 11th user,
            we will charge the credit card on file on the 1st of the month.
            You will be sent an email before you are charged. Please view our
            <ExternalLink
              :class="$style.link"
              href="https://jumpcloud.com/fair-use-policy"
              :showExternalLinkIcon="false"
              text="Fair Use Policy."
              @click="handlePaymentLinkClicked('/fair-use-policy', modalName)"
            />
          </span>
        </span>

        If you have any questions regarding billing please contact
        <ExternalLink
          :class="$style.link"
          href="mailto:billing@jumpcloud.com"
          :showExternalLinkIcon="false"
          text="billing@jumpcloud.com"
          @click="handlePaymentLinkClicked('billing@jumpcloud.com', modalName)"
        />.
      </ParagraphBody>

      <Button
        :class="$style.ctaButton"
        data-test-id="PaymentOutroModal_actionButton"
        text="Ok, Got It"
        @click="handleCloseButton"
      />
    </section>
    <CloseButton :handleClose="handleCloseButton" />
  </div>
</template>

<script>
import {
  Button,
  CustomIcon,
  HeadingHuge,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { eventLocations, eventProperties, paymentModalNames } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapGetters } from 'vuex';
import CloseButton from 'components/Buttons/CloseButton.vue';
import CompleteIcon from 'public/img/growth/self-checkout-complete-step-check.svg?inline';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ExternalLink from 'components/ExternalLink.vue';
import FilledIcon from 'public/img/growth/self-checkout-filled-step-check.svg?inline';
import PaymentStepMixin from 'client/Payments/mixins/PaymentStepMixin';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import UncompleteIcon from 'public/img/growth/self-checkout-uncomplete-step-check.svg?inline';

const {
  getProductCodeByType,
  SubscriptionTypes,
} = SubscriptionConstants;

const pricingModule = 'PricingModule';
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const {
  description,
  location,
  section,
  text: textEventProp,
} = eventProperties;

export default {
  name: 'PaymentOutroModal',

  components: {
    Button,
    CloseButton,
    CompleteIcon,
    CustomIcon,
    ExternalLink,
    FilledIcon,
    HeadingHuge,
    ParagraphBody,
    UncompleteIcon,
  },

  EventHelper,

  mixins: [PaymentStepMixin],

  props: {
    showUserLimitWarning: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      modalName: paymentModalNames.paymentOutroModal, // to send with Segment events
    };
  },

  computed: {
    ...mapGetters(pricingModule, {
      isAnnual: 'getIsAnnual',
      userCount: 'getUserCount',
    }),

    ...mapGetters(settingsModule, {
      user: 'getUser',
    }),

    checkoutSteps() {
      const steps = [
        {
          text: 'Payment details added',
          alwaysShowVerticalLine: true,
        },
        {
          text: this.planSelectedStep,
          alwaysShowVerticalLine: true,
        },
        {
          text: 'Get started! Add your users and begin to configure your JumpCloud environment',
          isActive: true,
          annualOnly: true,
        },
      ];

      // Currently there are no monthly only steps, so if in annual mode show all
      return steps.filter((step) => this.isAnnual || !step.annualOnly);
    },

    isAnnualWithAutomaticSelfServeFlagOff() {
      return this.isAnnual && !this.$hasFeatureFlag(this.$featureFlags.automaticAnnualSelfServe);
    },

    isAnnualWithAutomaticSelfServeFlagOn() {
      return this.isAnnual && this.$hasFeatureFlag(this.$featureFlags.automaticAnnualSelfServe);
    },

    plan() {
      let plan;

      if (this.subscription.productCode === getProductCodeByType(SubscriptionTypes.alaCarte)) {
        const addOns = this.subscription
          .addOns
          .map(addOn => addOn.displayName)
          .join(', ');
        const feature = this.subscription.addOns.length > 1 ? 'features' : 'feature';
        plan = `${addOns} ${feature}`;
      } else {
        plan = `${this.subscription.displayName} Plan`;
      }

      return plan;
    },

    planSelectedStep() {
      let planSelected = `${this.plan} selected`;
      if (this.isAnnual) planSelected += ` for ${this.userCount} users`;
      return planSelected;
    },
  },

  methods: {
    handleCloseButton() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Payment Thank You Modal dismissed',
        [location]: eventLocations.paymentThankYouModal,
        [section]: 'Payment Thank You Modal, footer or X',
        [textEventProp]: 'Okay, Got It',
      });
      this.handleCloseAndRedirect();
    },

    showVerticalLine(step) {
      return step.alwaysShowVerticalLine
        || (step.annualOnly && !this.$hasFeatureFlag(this.$featureFlags.automaticAnnualSelfServe));
    },
  },
};
</script>

<style scoped module>
@import '../css/newPaymentModals.css';

.container {
  align-items: center;
  display: flex;
  height: 95vh;
  justify-content: center;
}

.modalContent {
  display: flex;
  flex-direction: column;
  width: 650px;
}

.biggerGap.biggerGap {
  margin-top: var(--jc-spacer-large);
}

.checklistContainer {
  background-color: rgba(241, 243, 244, 0.4);
  border-radius: var(--jc-spacer-small);
  display: flex;
  flex-direction: column;
  margin-bottom: var(--jc-spacer);
  padding: var(--jc-spacer-large);
}

.checklistRow {
  align-items: flex-start;
  display: flex;
  gap: var(--jc-spacer);
  line-height: 1.5;
}

.checkoutLine {
  flex-direction: column;
}

.alignSelfCenter {
  align-self: center;
}

.ctaButton {
  align-self: flex-end;
  width: fit-content;
}

.checkoutIcon {
  flex-shrink: 0;
}

.haveQuestionsText {
  margin-top: 2.5rem;
  text-align: center;
}

.verticalLine {
  border-left: 1px solid var(--jcGrey300);
  height: 2.5rem;
  margin: 0.25rem 0.75rem;
}
</style>
